/**
 * Una regla de filtro (fuente o fijo del gráfico): campo, condición, valor.
 */
import React from 'react';
import { studio, ink } from '../studioUi';
import FilterDropdown from './FilterDropdown';
import { opsForFixedFilter } from './filterUtils';

function splitValue(item) {
    if (Array.isArray(item.value)) return item.value.map(String);
    return String(item.value || '').split(/[,;]/).map((part) => part.trim()).filter(Boolean);
}

function opHint(op) {
    const map = {
        eq: 'Coincide exactamente',
        neq: 'Todo excepto este valor',
        in: 'Uno o varios de la lista',
        not_in: 'Ninguno de estos',
        contains: 'La celda incluye este texto',
        not_contains: 'La celda no incluye este texto',
        starts_with: 'Empieza por…',
        ends_with: 'Termina en…',
        gt: 'Estrictamente mayor',
        gte: 'Mayor o igual',
        lt: 'Estrictamente menor',
        lte: 'Menor o igual',
        between: 'Incluye el inicio y el fin',
        empty: 'Celdas en blanco',
        not_empty: 'Celdas con algún valor',
    };
    return map[op] || '';
}

export default function FilterRuleCard({
    item,
    columns = [],
    dataSource,
    onChange,
    onRemove,
    showColumn = false,
    compact = false,
}) {
    const colMeta = columns.find((col) => col.name === item.column);
    const ops = opsForFixedFilter(item, colMeta);
    const op = ops.some((row) => row.value === item.operator) ? item.operator : ops[0].value;
    const selectedValues = splitValue(item);
    const betweenParts = op === 'between'
        ? (Array.isArray(item.value) ? item.value.map(String) : selectedValues)
        : selectedValues;
    const inferred = colMeta?.inferred_type || item.inferred_type || 'string';
    const isDate = inferred === 'date';
    const isNumber = inferred === 'number' || item.role === 'metric';
    const needsNone = op === 'empty' || op === 'not_empty';
    const needsText = ['contains', 'not_contains', 'starts_with', 'ends_with'].includes(op);
    const needsMulti = op === 'in' || op === 'not_in';
    const control = compact ? ui.inputCompact : ui.input;
    const select = compact ? ui.selectCompact : ui.select;

    const setBetween = (left, right) => onChange({ value: [left || '', right || ''].join(', ') });

    const valueControl = needsNone ? null : needsText ? (
        <input
            value={item.value || ''}
            placeholder="Texto"
            onChange={(e) => onChange({ value: e.target.value })}
            style={control}
            title={opHint(op)}
        />
    ) : op === 'between' ? (
        <div style={ui.row}>
            {isDate ? (
                <>
                    <input type="date" value={betweenParts[0] || ''} onChange={(e) => setBetween(e.target.value, betweenParts[1] || '')} style={control} />
                    <input type="date" value={betweenParts[1] || ''} onChange={(e) => setBetween(betweenParts[0] || '', e.target.value)} style={control} />
                </>
            ) : isNumber ? (
                <>
                    <input type="number" placeholder="Desde" value={betweenParts[0] || ''} onChange={(e) => setBetween(e.target.value, betweenParts[1] || '')} style={control} />
                    <input type="number" placeholder="Hasta" value={betweenParts[1] || ''} onChange={(e) => setBetween(betweenParts[0] || '', e.target.value)} style={control} />
                </>
            ) : (
                <>
                    <FilterDropdown compact multiple={false} placeholder="Desde" value={betweenParts[0] ? [betweenParts[0]] : []} options={colMeta?.values || []} column={item.column} dataSource={dataSource} onChange={(next) => setBetween(next[0] || '', betweenParts[1] || '')} />
                    <FilterDropdown compact multiple={false} placeholder="Hasta" value={betweenParts[1] ? [betweenParts[1]] : []} options={colMeta?.values || []} column={item.column} dataSource={dataSource} onChange={(next) => setBetween(betweenParts[0] || '', next[0] || '')} />
                </>
            )}
        </div>
    ) : isDate && ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'].includes(op) ? (
        <input type="date" value={selectedValues[0] || ''} onChange={(e) => onChange({ value: e.target.value })} style={control} />
    ) : isNumber && ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'].includes(op) ? (
        <input type="number" placeholder="Valor" value={item.value || ''} onChange={(e) => onChange({ value: e.target.value })} style={control} />
    ) : (
        <FilterDropdown
            compact
            multiple={needsMulti}
            placeholder={needsMulti ? 'Valores' : 'Valor'}
            value={needsMulti ? selectedValues : selectedValues.slice(0, 1)}
            options={colMeta?.values || []}
            column={item.column}
            dataSource={dataSource}
            onChange={(next) => onChange({ value: needsMulti ? next.join(', ') : (next[0] || '') })}
        />
    );

    const columnSelect = showColumn ? (
        <select
            value={item.column}
            onChange={(e) => {
                const nextCol = columns.find((col) => col.name === e.target.value);
                onChange({
                    column: e.target.value,
                    inferred_type: nextCol?.inferred_type || item.inferred_type,
                    value: '',
                });
            }}
            style={select}
        >
            {columns.map((col) => (
                <option key={col.name} value={col.name}>{col.name}</option>
            ))}
        </select>
    ) : (
        <strong style={ui.field}>{item.column}</strong>
    );

    const opSelect = (
        <select
            value={op}
            onChange={(e) => onChange({ operator: e.target.value, value: '' })}
            style={select}
            title={opHint(op)}
        >
            {ops.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
        </select>
    );

    if (compact) {
        return (
            <div style={ui.compactRow}>
                {columnSelect}
                {opSelect}
                <div style={ui.compactValue}>{valueControl}</div>
                <button type="button" style={ui.remove} onClick={onRemove} title="Quitar regla">×</button>
            </div>
        );
    }

    return (
        <div style={ui.card}>
            <div style={ui.top}>
                {columnSelect}
                <button type="button" style={ui.remove} onClick={onRemove} title="Quitar regla">×</button>
            </div>
            <span style={ui.kind}>{item.role === 'metric' ? 'Métrica' : isDate ? 'Fecha' : isNumber ? 'Número' : 'Texto'}</span>
            <label style={studio.fieldLabel}>Condición</label>
            {opSelect}
            {opHint(op) ? <p style={ui.hint}>{opHint(op)}</p> : null}
            {valueControl}
        </div>
    );
}

const ui = {
    card: {
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: '10px 10px 12px',
        backgroundColor: ink.surface,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: 12,
    },
    compactRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        width: '100%',
        boxSizing: 'border-box',
        padding: '4px 6px 4px 4px',
        backgroundColor: ink.surface,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: 999,
        minWidth: 0,
    },
    compactValue: { flex: 1, minWidth: 72 },
    top: { display: 'flex', alignItems: 'center', gap: 8 },
    field: { flex: 1, fontSize: '0.84rem', color: ink.text, fontWeight: 700, minWidth: 0 },
    kind: {
        alignSelf: 'flex-start',
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: ink.accentText,
        backgroundColor: ink.accentSoft,
        borderRadius: 999,
        padding: '2px 8px',
    },
    hint: { margin: 0, fontSize: '0.72rem', color: ink.faint, lineHeight: 1.35 },
    select: { ...studio.controlCompact, width: '100%', boxSizing: 'border-box' },
    input: { ...studio.controlCompact, width: '100%', boxSizing: 'border-box', flex: 1, minWidth: 0 },
    selectCompact: {
        ...studio.controlCompact,
        width: 'auto',
        flex: '0 1 110px',
        minWidth: 80,
        minHeight: 28,
        height: 28,
        padding: '2px 10px',
        borderRadius: 999,
        fontSize: '0.72rem',
        boxSizing: 'border-box',
    },
    inputCompact: {
        ...studio.controlCompact,
        width: '100%',
        minHeight: 28,
        height: 28,
        padding: '2px 10px',
        borderRadius: 999,
        fontSize: '0.72rem',
        boxSizing: 'border-box',
        flex: 1,
        minWidth: 0,
    },
    row: { display: 'flex', gap: 6, alignItems: 'stretch', minWidth: 0, flex: 1 },
    remove: {
        borderWidth: 0,
        borderStyle: 'solid',
        borderColor: 'transparent',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        color: ink.danger,
        fontSize: '1.15rem',
        lineHeight: 1,
        padding: '0 2px',
        flexShrink: 0,
        fontFamily: 'inherit',
    },
};
